import { compact, flatten, uniqBy } from 'lodash';

import { AIRPORT, SEAPORT, TRANSFER_STOP } from '@/lib/constants';
import { ItineraryLegModel } from '@/lib/models/itinerary/types';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { LocationInterface, MapMarker, MarkerSize } from '@/types';
import { formatRouteLocation } from '@/utils/format';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { getMapMarkerIcon } from '@/utils/mapping';
import { transformSegmentLocation } from '@/utils/transform/map-route';

export const getItineraryMapMarkers = (
  legs: ItineraryLegModel[],
  locationDictionary: LocationDictionaryModel,
): MapMarker[] => {
  // Get unique sequential ids
  const locations = legs.map((leg) => {
    // Handle From To
    const fromTo = [
      formatRouteLocation(leg.fromId, locationDictionary),
      formatRouteLocation(leg.toId, locationDictionary),
    ];
    const [from, to] = fromTo.map((location) => transformSegmentLocation(location));

    // Handle stops
    const stopIds = leg.stopIds || [];
    const stops = stopIds.map((id) => formatRouteLocation(id, locationDictionary));
    const stopLocations = stops.map((location) => transformSegmentLocation(location, TRANSFER_STOP));

    return compact([from, ...stopLocations, to]) as LocationInterface[];
  });

  const uniqLocations = uniqBy(flatten(locations), 'name');

  const mapMarkers = uniqLocations.reduce((accum, location, index) => {
    if (!location.location) {
      return accum;
    }

    const isStartOrEndPoint = index === 0 || index === uniqLocations.length - 1;
    const isAirportOrSeaport = location.type === AIRPORT || location.type === SEAPORT;
    const isStop = location.transferType === TRANSFER_STOP;
    const size = isAirportOrSeaport && !isStop ? 'lg' : 'md';
    const label = isAirportOrSeaport ? location.identifier?.value : location.name;
    const markerColor = isAirportOrSeaport ? getTransportColor(location.type) : 'lightBlue.500';

    const newMarker: MapMarker = {
      id: `M_${location.placeId}_${index}`,
      size: (isStop ? 'sm' : size) as MarkerSize,
      location: location.location,
      nodeType: location.type,
      label: label || '',
      color: markerColor,
      icon: isAirportOrSeaport && !isStop ? getMapMarkerIcon(location.type, location.transferType) : '',
      active: isStartOrEndPoint,
    };

    return [...accum, newMarker];
  }, [] as MapMarker[]);

  return mapMarkers;
};
